/**
 * Data layer for the `/lists` routes (web/docs/data-layer.md). Mirrors the
 * mobile real client's list methods (`app/src/data/api-real.ts`) over
 * `src/api/http.ts` + `@shared` mappers.
 *
 * Lists have NO shared cache store — the same contract as the app
 * (web/docs/screens.md §/lists): every mutation refetches the affected keys
 * (`['lists']`, `['list', id]`, `['listShows', id]`) instead of patching
 * optimistically. Reads degrade on `404` like the app does.
 */

import { useRef } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { toast } from 'sonner';

import { apiDelete, apiGet, apiPost, apiPut, ApiError } from '@/api/http';
import { translate } from '@/i18n/locale';

import type {
  ApiList,
  ApiListDeletedResponse,
  ApiListItem,
  ApiListMembershipResponse,
  ApiListRenamedResponse,
  ApiListsResponse,
  ApiListShowsPage,
  ApiShowsPage,
} from '@shared/api-types';
import { mapList, mapListItem, mapShowToTitle } from '@shared/mappers';
import type { Title, TitleType, UserList, UserListItem } from '@shared/types';

export const listsKey = ['lists'] as const;
export const listKey = (id: string) => ['list', id] as const;
export const listShowsKey = (id: string) => ['listShows', id] as const;

/** Same page size the mobile app uses (`app/src/data/api-real.ts`). */
export const LIST_SHOWS_PAGE_SIZE = 50;

/** One cursor page of `GET /v1/lists/:id/shows` (local shape — `client.ts` is off-limits). */
export interface ListShowsPage {
  list: UserList;
  items: UserListItem[];
  lastKey?: string;
}

async function or404Null<T>(fn: () => Promise<T>): Promise<T | null> {
  try {
    return await fn();
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

/** `GET /v1/lists` → the user's collections in display order. */
export async function fetchLists(): Promise<UserList[]> {
  const res = await or404Null(() => apiGet<ApiListsResponse>('/v1/lists'));
  return (res?.lists ?? []).filter((l): l is ApiList => Boolean(l)).map(mapList);
}

/**
 * One list's metadata. There is no bare `GET /v1/lists/:id` — the shows
 * endpoint carries the list item, so ask for a single row. `null` when the
 * list does not exist.
 */
export async function fetchList(id: string): Promise<UserList | null> {
  const res = await or404Null(() =>
    apiGet<ApiListShowsPage>(`/v1/lists/${id}/shows`, { query: { per_page: 1 } }),
  );
  return res ? mapList(res.list) : null;
}

/** One membership page; `null` when the list does not exist. */
export async function fetchListShowsPage(
  id: string,
  lastKey?: string,
): Promise<ListShowsPage | null> {
  const res = await or404Null(() =>
    apiGet<ApiListShowsPage>(`/v1/lists/${id}/shows`, {
      query: { per_page: LIST_SHOWS_PAGE_SIZE, last_key: lastKey },
    }),
  );
  if (!res) return null;
  return {
    list: mapList(res.list),
    items: (res.shows ?? []).filter((i): i is ApiListItem => Boolean(i)).map(mapListItem),
    lastKey: res.last_key ?? undefined,
  };
}

/** `GET /v1/shows/search` → mapped titles (sparse catalog rows, page 1). */
export async function searchTitles(query: string): Promise<{ titles: Title[]; total: number }> {
  const res = await or404Null(() =>
    apiGet<ApiShowsPage>('/v1/shows/search', {
      query: { q: query, page: 1, per_page: 20 },
    }),
  );
  const titles = (res?.shows ?? [])
    .filter((s): s is NonNullable<typeof s> => Boolean(s))
    .map(mapShowToTitle);
  return { titles, total: res?.total ?? titles.length };
}

// --- queries ------------------------------------------------------------------

export function useLists() {
  return useQuery({ queryKey: listsKey, queryFn: fetchLists });
}

/**
 * Header metadata (name, description, count) for one list. Opened from the
 * index, the cached `['lists']` row stands in while it fetches, so the
 * header renders at once and the grid skeleton knows its count (no extra
 * request; a placeholder is never cached as this key's data).
 */
export function useList(id: string) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: listKey(id),
    queryFn: () => fetchList(id),
    enabled: id !== '',
    placeholderData: () => qc.getQueryData<UserList[]>(listsKey)?.find((list) => list.id === id),
  });
}

/** The paginated membership grid. 404 surfaces as an `ApiError` in `error`. */
export function useListShows(id: string) {
  return useInfiniteQuery({
    queryKey: listShowsKey(id),
    queryFn: ({ pageParam }) => fetchListShowsPage(id, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage?.lastKey,
    enabled: id !== '',
  });
}

/** Add-screen search; gated on a non-empty (debounced) query. */
export function useTitleSearch(query: string) {
  const q = query.trim();
  return useQuery({
    queryKey: ['titleSearch', q] as const,
    queryFn: () => searchTitles(q),
    enabled: q.length > 0,
  });
}

/** Every membership id the add screen has seen (loaded pages only). */
export function membershipIds(pages: (ListShowsPage | null)[] | undefined): Set<string> {
  const ids = new Set<string>();
  for (const page of pages ?? []) {
    if (!page) continue;
    for (const item of page.items) ids.add(item.showId);
  }
  return ids;
}

// --- mutations (refetch on settle, toast + Retry on failure) --------------------

/**
 * Lists never patch the cache optimistically — invalidate the affected keys
 * and let the refetch converge (screens.md §/lists). Failure gets a toast
 * with a Retry action (ui-ux.md §7); the ref makes Retry re-enter the same
 * mutation without closing over a stale `mutate`.
 */
function useRefetchMutation<TData, TVariables>(
  options: UseMutationOptions<TData, Error, TVariables, unknown>,
  errorMessage: (vars: TVariables) => string,
) {
  const mutateRef = useRef<(vars: TVariables) => void>(() => {});
  const { onError, ...rest } = options;
  const mutation = useMutation<TData, Error, TVariables, unknown>({
    ...rest,
    onError: (error, vars, onMutateResult, context) => {
      onError?.(error, vars, onMutateResult, context);
      toast.error(errorMessage(vars), {
        action: { label: translate('common.retry'), onClick: () => mutateRef.current(vars) },
      });
    },
  });
  mutateRef.current = mutation.mutate;
  return mutation;
}

function invalidateListScope(qc: ReturnType<typeof useQueryClient>, id: string) {
  void qc.invalidateQueries({ queryKey: listsKey });
  void qc.invalidateQueries({ queryKey: listKey(id) });
  void qc.invalidateQueries({ queryKey: listShowsKey(id) });
}

/** `POST /v1/lists`. The dialog awaits this so it can surface 400/409 inline. */
export function useCreateList() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { name: string; description?: string }) => {
      const res = await apiPost<ApiList>('/v1/lists', {
        name: vars.name,
        description: vars.description,
      });
      return mapList(res);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: listsKey });
    },
  });
}

/** `PUT /v1/lists/:id` — rename / update the description. */
export function useRenameList(id: string) {
  const qc = useQueryClient();
  return useRefetchMutation(
    {
      mutationFn: (vars: { name: string; description?: string }) =>
        apiPut<ApiListRenamedResponse>(`/v1/lists/${id}`, vars),
      onSettled: () => invalidateListScope(qc, id),
    },
    () => translate('lists.error.rename'),
  );
}

/** `DELETE /v1/lists/:id` — the list and its memberships (titles stay put). */
export function useDeleteList(id: string) {
  const qc = useQueryClient();
  return useRefetchMutation(
    {
      mutationFn: () => apiDelete<ApiListDeletedResponse>(`/v1/lists/${id}`),
      onSettled: () => {
        void qc.invalidateQueries({ queryKey: listsKey });
        qc.removeQueries({ queryKey: listKey(id) });
        qc.removeQueries({ queryKey: listShowsKey(id) });
      },
    },
    () => translate('lists.error.delete'),
  );
}

/** `PUT /v1/lists/reorder` — persist the full order, top first. */
export function useReorderLists() {
  const qc = useQueryClient();
  return useRefetchMutation(
    {
      mutationFn: (listIds: string[]) =>
        apiPut<{ reordered: boolean }>('/v1/lists/reorder', { list_ids: listIds }),
      onSettled: () => {
        void qc.invalidateQueries({ queryKey: listsKey });
      },
    },
    () => translate('lists.error.reorder'),
  );
}

/** `PUT /v1/lists/:id/shows/reorder` — persist the full member order. */
export function useReorderListShows(id: string) {
  const qc = useQueryClient();
  return useRefetchMutation(
    {
      mutationFn: (showIds: string[]) =>
        apiPut<{ reordered: boolean }>(`/v1/lists/${id}/shows/reorder`, { show_ids: showIds }),
      onSettled: () => invalidateListScope(qc, id),
    },
    () => translate('lists.error.reorderShows'),
  );
}

/** `PUT /v1/lists/:id/shows/:showId?type=` — add one title (type: TMDB ids overlap). */
export function useAddToList(id: string) {
  const qc = useQueryClient();
  return useRefetchMutation(
    {
      mutationFn: ({ showId, type }: { showId: string; type?: TitleType }) =>
        apiPut<ApiListMembershipResponse>(`/v1/lists/${id}/shows/${showId}`, undefined, {
          query: { type },
        }),
      onSettled: () => invalidateListScope(qc, id),
    },
    () => translate('lists.error.add'),
  );
}

/** `DELETE /v1/lists/:id/shows/:showId` — remove one title. */
export function useRemoveFromList(id: string) {
  const qc = useQueryClient();
  return useRefetchMutation(
    {
      mutationFn: (showId: string) =>
        apiDelete<ApiListMembershipResponse>(`/v1/lists/${id}/shows/${showId}`),
      onSettled: () => invalidateListScope(qc, id),
    },
    () => translate('lists.error.remove'),
  );
}
